import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import Header from "@/components/Header";
import HelpFab from "@/components/HelpFab";
import AdminSupportChats from "@/components/AdminSupportChats";
import AuditLogCard from "@/components/AuditLogCard";
import EmailHealthCard from "@/components/EmailHealthCard";
import OriginChip from "@/components/OriginChip";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import LoginActivityModal from "@/components/LoginActivityModal";
import { Eye, Users, Search, Shield, Mail, Clock, AlertTriangle, KeyRound, Copy, Check, UserX, UserCheck, X, ShieldCheck, GraduationCap, Pencil, Fingerprint, Rocket, Tag, Building2 } from "lucide-react";
import { toast } from "sonner";

const ROLE_FILTERS = [
  { key: "all", label: "All accounts" },
  { key: "participant", label: "Members" },
  { key: "affiliate_manager", label: "Leaders" },
  { key: "admin", label: "Admins" },
];

const ROLE_CHIP = {
  admin: { label: "Admin", bg: "rgba(220,38,38,0.10)", color: "#B91C1C" },
  affiliate_manager: { label: "Leader", bg: "rgba(124,58,237,0.10)", color: "#7C3AED" },
  participant: { label: "Member", bg: "rgba(64,96,255,0.10)", color: "#4060FF" },
};

function relTime(iso) {
  if (!iso) return "Never";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "—";
  const diff = (Date.now() - t) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 86400 * 30) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
}

export default function AdminMembers() {
  const { user, startImpersonation } = useAuth();
  const navigate = useNavigate();
  const [members, setMembers] = useState([]);
  const [audit, setAudit] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [onlyNoWallet, setOnlyNoWallet] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [otps, setOtps] = useState([]);
  const [copiedCode, setCopiedCode] = useState(null);
  const [resetState, setResetState] = useState(null); // { member, mode: 'choose'|'done', pw, custom, error, busy }
  const [activity, setActivity] = useState(null);

  const ROLE_WORDS = {
    participant: "a regular member (sees only their own branch once linked)",
    affiliate_manager: "a Leader — they see ONLY their own wallet + everyone below it",
    admin: "an admin — they see the WHOLE business",
  };
  const onSetRole = async (m, role) => {
    if (!role || role === m.role) return;
    if (!window.confirm(`Change ${m.email} to ${ROLE_WORDS[role]}?`)) return;
    setBusyId(m.id);
    try {
      const { data } = await api.post(`/admin/members/${m.id}/set-role`, { role });
      toast.success(`${m.email} role updated`);
      if (role !== "admin" && !data.linked_node) {
        toast.warning("They're not linked to a wallet tile yet — open their tile on the Org chart → 'Link existing account' so they see their branch.", { duration: 10000 });
      }
      fetchAll();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusyId(null); }
  };

  const onSetName = async (m) => {
    const next = window.prompt(`Rename ${m.name || m.email}?\n\nThis changes their display name only — their login email and wallet tiles stay exactly as they are.`, m.name || "");
    if (next == null) return;
    const name = next.trim();
    if (!name || name === (m.name || "")) return;
    setBusyId(m.id);
    try {
      await api.post(`/admin/members/${m.id}/set-name`, { name });
      toast.success(`Renamed to ${name}`);
      fetchAll();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusyId(null); }
  };

  const onSetEmail = async (m) => {
    const next = window.prompt(`Change email for ${m.name || m.email}?\n\nThis updates their login email AND every wallet tile linked to this account so Wallets and Members stay in sync.`, m.email);
    if (next == null) return;
    const email = next.trim().toLowerCase();
    if (!email || email === (m.email || "").toLowerCase()) return;
    setBusyId(m.id);
    try {
      const { data } = await api.post(`/admin/members/${m.id}/set-email`, { email });
      toast.success(`Email updated${data.synced_wallets ? ` · ${data.synced_wallets} wallet tile${data.synced_wallets > 1 ? "s" : ""} synced` : ""}`);
      fetchAll();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusyId(null); }
  };

  const fetchAll = async () => {
    setLoading(true);
    setError("");
    try {
      const [m, a] = await Promise.all([
        api.get("/admin/members"),
        api.get("/admin/impersonate/audit", { params: { limit: 25 } }),
      ]);
      setMembers(m.data);
      setAudit(a.data);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Could not load members.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const [m, a, o] = await Promise.all([
          api.get("/admin/members"),
          api.get("/admin/impersonate/audit", { params: { limit: 25 } }),
          api.get("/admin/otp-peek"),
        ]);
        setMembers(m.data);
        setAudit(a.data);
        setOtps(o.data);
      } catch (err) {
        setError(formatApiError(err.response?.data?.detail) || "Could not load members.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Re-poll OTPs every 8 s so the admin can see new sign-in codes appear live.
  useEffect(() => {
    const t = setInterval(async () => {
      try {
        const { data } = await api.get("/admin/otp-peek");
        setOtps(data);
      } catch { /* polling errors are silent */ }
    }, 8000);
    return () => clearInterval(t);
  }, []);

  const copyCode = async (code) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopiedCode(code);
      setTimeout(() => setCopiedCode((c) => (c === code ? null : c)), 1500);
    } catch { /* clipboard unsupported — silently skip */ }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return members.filter((m) => {
      // The demo account is a super-admin-only tool — hide it from other admins.
      if (m.is_demo && !user?.is_super_admin) return false;
      if (roleFilter !== "all" && m.role !== roleFilter) return false;
      // "No wallet yet" = doesn't have their own synced CaryPact wallet + link,
      // so they don't qualify as a member under our rule.
      if (onlyNoWallet && m.has_live_wallet) return false;
      if (!q) return true;
      return (
        m.email.toLowerCase().includes(q) ||
        (m.name || "").toLowerCase().includes(q) ||
        (m.node_label || "").toLowerCase().includes(q)
      );
    });
  }, [members, search, roleFilter, onlyNoWallet, user]);

  const onImpersonate = async (member) => {
    if (member.is_super_admin) {
      toast.error("The super admin can't be impersonated.");
      return;
    }
    if (!window.confirm(
      `View the app as ${member.email}?\n\nYou'll see exactly what they see, with full access — anything you do is applied for real and logged to the audit trail.`,
    )) return;
    setBusyId(member.id);
    try {
      await startImpersonation(member.id);
      toast.success(`Now viewing as ${member.email}`);
      navigate("/dashboard");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not start impersonation.");
    } finally {
      setBusyId(null);
    }
  };

  const onToggleStatus = async (member) => {
    const next = (member.status || "Active") === "Active" ? "Inactive" : "Active";
    const verb = next === "Inactive" ? "Deactivate" : "Activate";
    if (!window.confirm(
      `${verb} ${member.email}?\n\n${next === "Inactive"
        ? "They will no longer be able to log in until you reactivate them. Their org-chart tile and all data are preserved."
        : "They will be able to log in again with their existing password."}`,
    )) return;
    setBusyId(member.id);
    try {
      await api.post(`/admin/members/${member.id}/set-status`, { status: next });
      toast.success(`${member.email} is now ${next}`);
      // Optimistic update so the badge flips instantly.
      setMembers((prev) => prev.map((u) => (u.id === member.id ? { ...u, status: next } : u)));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || `Could not ${verb.toLowerCase()} account.`);
    } finally {
      setBusyId(null);
    }
  };

  const onToggleOnboarding = async (member, approve) => {
    if (!window.confirm(approve
      ? `Unlock ${member.email}?\n\nThey'll get full access to the app immediately.`
      : `Re-lock ${member.email}?\n\nThey'll be restricted to the Getting Started checklist until you approve them again.`,
    )) return;
    setBusyId(member.id);
    try {
      await api.post(`/admin/members/${member.id}/set-onboarding`, { approved: approve });
      toast.success(approve ? `${member.email} unlocked — full access granted.` : `${member.email} locked to onboarding.`);
      setMembers((prev) => prev.map((u) => (u.id === member.id ? {
        ...u,
        onboarding_required: true,
        onboarding_locked: !approve,
        onboarding_approved_at: approve ? new Date().toISOString() : null,
        onboarding_submitted_at: approve ? u.onboarding_submitted_at : null,
      } : u)));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not update onboarding.");
    } finally {
      setBusyId(null);
    }
  };

  const onToggleMomentum = async (member, next) => {
    setBusyId(member.id);
    // Optimistic — the checkbox flips instantly, reverts if the save fails.
    setMembers((prev) => prev.map((u) => (u.id === member.id ? { ...u, momentum_access: next } : u)));
    try {
      await api.post(`/admin/members/${member.id}/momentum-access`, { enabled: next });
      toast.success(next ? `${member.email} can now see Operation Momentum` : `Operation Momentum hidden from ${member.email}`);
    } catch (err) {
      setMembers((prev) => prev.map((u) => (u.id === member.id ? { ...u, momentum_access: !next } : u)));
      toast.error(formatApiError(err.response?.data?.detail) || "Could not update access.");
    } finally {
      setBusyId(null);
    }
  };

  // Partner is ELIGIBILITY, not the firm itself: the firm is created on the Partners
  // screen, which won't accept one without an eligible member behind it.
  const onTogglePartner = async (member, next) => {
    setBusyId(member.id);
    setMembers((prev) => prev.map((u) => (u.id === member.id ? { ...u, partner_access: next } : u)));
    try {
      const { data } = await api.post(`/admin/members/${member.id}/partner-access`, { enabled: next });
      const firms = data?.firms_changed || [];
      if (next) {
        toast.success(firms.length
          ? `${member.email} is a partner again — ${firms.join(", ")} back online`
          : `${member.email} can run a partner firm — create it on the Partners screen`);
      } else {
        toast.success(firms.length
          ? `Partner switched off — ${firms.join(", ")} suspended, every client and statement kept`
          : `Partner switched off for ${member.email}`);
      }
    } catch (err) {
      setMembers((prev) => prev.map((u) => (u.id === member.id ? { ...u, partner_access: !next } : u)));
      toast.error(formatApiError(err.response?.data?.detail) || "Could not update access.");
    } finally {
      setBusyId(null);
    }
  };

  const onToggleMarketing = async (member, next) => {
    setBusyId(member.id);
    setMembers((prev) => prev.map((u) => (u.id === member.id ? { ...u, marketing_access: next } : u)));
    try {
      await api.post(`/admin/members/${member.id}/marketing-access`, { enabled: next });
      toast.success(next
        ? `${member.email} is now a marketing manager — they can build UTM links`
        : `Marketing links turned off for ${member.email}`);
    } catch (err) {
      setMembers((prev) => prev.map((u) => (u.id === member.id ? { ...u, marketing_access: !next } : u)));
      toast.error(formatApiError(err.response?.data?.detail) || "Could not update access.");
    } finally {
      setBusyId(null);
    }
  };


  const openResetModal = (member) => {
    if (member.is_super_admin) {
      toast.error("The super admin's password is changed via 'Change password' while logged in.");
      return;
    }
    setResetState({ member, mode: "choose", pw: "", custom: "", error: "", busy: false });
  };

  const submitReset = async () => {
    if (!resetState) return;
    setResetState((s) => ({ ...s, busy: true, error: "" }));
    try {
      const body = resetState.custom.trim() ? { password: resetState.custom.trim() } : {};
      const { data } = await api.post(`/admin/members/${resetState.member.id}/reset-password`, body);
      setResetState((s) => ({ ...s, busy: false, mode: "done", pw: data.password, error: "" }));
      // Reactivated as a side-effect server-side — keep the table in sync.
      setMembers((prev) => prev.map((u) => (u.id === resetState.member.id ? { ...u, status: "Active", email_verified: true } : u)));
    } catch (err) {
      setResetState((s) => ({ ...s, busy: false, error: formatApiError(err.response?.data?.detail) || "Could not reset password." }));
    }
  };

  const copyResetPw = async () => {
    if (!resetState?.pw) return;
    try {
      await navigator.clipboard.writeText(resetState.pw);
      toast.success("Password copied");
    } catch { /* clipboard unsupported — silently skip */ }
  };

  // Guard: only admins can see this page.
  if (user && user.role !== "admin") return <Navigate to="/dashboard" replace />;

  const stats = {
    total: members.length,
    members: members.filter((m) => m.role === "participant").length,
    leaders: members.filter((m) => m.role === "affiliate_manager").length,
    admins: members.filter((m) => m.role === "admin").length,
    noWallet: members.filter((m) => !m.has_live_wallet).length,
  };

  return (
    <div className="min-h-screen pb-16">
      <Header />
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 pt-10">
        <div className="mb-6 fade-up">
          <div className="pill mb-3"><span className="dot" />Admin · Members</div>
          <h1 className="font-display text-4xl md:text-5xl font-semibold tracking-tight">All members</h1>
          <p className="text-ink-2 text-sm mt-2 max-w-2xl">
            Every account in the system. Click the eye icon next to a member to <b>view the app exactly as they see it</b>.
            Impersonation sessions have <b>full access</b> and every action is <b>logged</b> in the audit trail below.
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          <div className="kpi-tile" data-testid="kpi-total"><div className="kpi-label">Total accounts</div><div className="kpi-num">{stats.total}</div></div>
          <div className="kpi-tile" data-testid="kpi-members"><div className="kpi-label">Members</div><div className="kpi-num" style={{ color: "#4060FF" }}>{stats.members}</div></div>
          <div className="kpi-tile" data-testid="kpi-leaders"><div className="kpi-label">Leaders</div><div className="kpi-num" style={{ color: "#7C3AED" }}>{stats.leaders}</div></div>
          <div className="kpi-tile" data-testid="kpi-admins"><div className="kpi-label">Admins</div><div className="kpi-num" style={{ color: "#B91C1C" }}>{stats.admins}</div></div>
          <button
            type="button"
            onClick={() => setOnlyNoWallet((v) => !v)}
            data-testid="kpi-no-wallet"
            title="Admins and leaders without their own synced CaryPact wallet. People who merely signed up aren't listed here at all — with no wallet they aren't members, so they sit in Trainees. Click to show only these."
            className="kpi-tile text-left transition-shadow"
            style={onlyNoWallet ? { boxShadow: "0 0 0 2px #B45309 inset", background: "rgba(180,83,9,0.06)" } : undefined}
          >
            <div className="kpi-label">No wallet yet</div>
            <div className="kpi-num" style={{ color: "#B45309" }}>{stats.noWallet}</div>
          </button>
        </div>

        {user?.is_super_admin && <EmailHealthCard />}

        <div className="glass p-4 mb-4 flex flex-col md:flex-row md:items-center gap-3">
          <div className="flex-1 flex items-center gap-2 px-3 py-2 rounded-xl" style={{ background: "rgba(15,18,38,0.04)" }}>
            <Search size={15} className="text-ink-3" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by email, name, or org-chart tile…"
              className="flex-1 bg-transparent outline-none text-sm"
              data-testid="members-search"
            />
          </div>
          <div className="flex items-center gap-1 p-1 rounded-xl glass">
            {ROLE_FILTERS.map((r) => (
              <button
                key={r.key}
                onClick={() => setRoleFilter(r.key)}
                data-testid={`role-filter-${r.key}`}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${roleFilter === r.key ? "bg-white text-ink shadow-sm" : "text-ink-2 hover:bg-white/50"}`}
              >
                {r.label}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={() => setOnlyNoWallet((v) => !v)}
            data-testid="filter-no-wallet"
            title="Show only accounts without their own synced CaryPact wallet. Self-registered logins with no wallet are in Trainees, not here."
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors inline-flex items-center gap-1.5 ${onlyNoWallet ? "bg-amber-500 text-white shadow-sm" : "glass text-amber-700 hover:bg-amber-50"}`}
          >
            <AlertTriangle size={13} /> No wallet yet ({stats.noWallet})
          </button>
        </div>

        <div className="glass overflow-hidden mb-8" data-testid="members-list-card">
          <div className="overflow-x-auto">
            <table className="modern members-compact" data-testid="members-table">
              <thead>
                <tr>
                  <th>Member</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th>Onboarding</th>
                  <th>Org-chart tile</th>
                  <th title="Which campaign link, plain share link or the registration page brought this member in — hover for the tags">Came from</th>
                  <th>Created</th>
                  {user?.is_super_admin && <th title="Extra access: Op Momentum reports, the UTM campaign-link builder, and running a white-label partner firm">Access</th>}
                  <th className="text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={user?.is_super_admin ? 9 : 8} className="text-center py-10 text-ink-3">Loading…</td></tr>
                ) : filtered.length === 0 ? (
                  <tr><td colSpan={user?.is_super_admin ? 9 : 8} className="text-center py-10 text-ink-3" data-testid="members-empty">
                    {members.length === 0 ? "No accounts yet. Grant logins from the Org Chart." : "No results match your filters."}
                  </td></tr>
                ) : filtered.map((m) => {
                  const chip = m.is_super_admin
                    ? { label: "Sup Admin", bg: "rgba(220,38,38,0.14)", color: "#991B1B" }
                    : ROLE_CHIP[m.role] || { label: m.role, bg: "rgba(15,18,38,0.06)", color: "#475569" };
                  const isAdmin = m.role === "admin";
                  const isDemo = m.is_demo;
                  // Only the super admin is protected from reset/deactivate. The
                  // Members list is already scoped to the viewer's team, and the
                  // backend re-checks team membership, so a regular admin gets the
                  // same actions as the super admin for their own downline.
                  const isProtectedAdmin = m.is_super_admin;
                  const isSelf = user && m.id === user.id;
                  const status = m.status || "Active";
                  const isInactive = status === "Inactive";
                  const isInvited = status === "Invited";
                  const statusChipClass = isInactive ? "chip-amber" : isInvited ? "chip-gray" : "chip-green";
                  return (
                    <tr key={m.id} data-testid={`member-row-${m.id}`} style={isInactive ? { opacity: 0.7 } : undefined}>
                      <td>
                        <div className="flex items-center gap-2.5">
                          <span className="w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0"
                            style={{ background: `${chip.color}1A`, color: chip.color }}>
                            {(m.name || m.email || "?").slice(0, 1).toUpperCase()}
                          </span>
                          <div className="leading-tight">
                            <div className="font-medium text-xs flex items-center gap-1">
                              <span>{m.name || "—"}</span>
                              {!isDemo && !(m.phone || "").trim() && (
                                <span className="inline-block px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider shrink-0"
                                  style={{ background: "rgba(245,158,11,0.14)", color: "#B45309" }}
                                  title="No phone number on file — they're asked for one on every login"
                                  data-testid={`member-no-phone-${m.id}`}>no phone</span>
                              )}
                              {!isDemo && !(m.is_super_admin && !user?.is_super_admin) && (
                                <button
                                  type="button"
                                  onClick={() => onSetName(m)}
                                  disabled={busyId === m.id}
                                  title="Rename — changes the display name only, never the email"
                                  data-testid={`edit-name-btn-${m.id}`}
                                  className="text-ink-3 hover:text-[#7C3AED] transition-colors disabled:opacity-40 shrink-0"
                                >
                                  <Pencil size={11} />
                                </button>
                              )}
                            </div>
                            <div className="text-[11px] text-ink-3 font-mono flex items-center gap-1">
                              <span title={m.email_verified ? "Email verified" : "Email not verified yet"}
                                className="shrink-0 w-1.5 h-1.5 rounded-full"
                                style={{ background: m.email_verified ? "#00863A" : "#CBD5E1" }}
                                data-testid={`member-verified-${m.id}`} />
                              <span>{m.email}</span>
                              {!isDemo && !(m.is_super_admin && !user?.is_super_admin) && (
                                <button
                                  type="button"
                                  onClick={() => onSetEmail(m)}
                                  disabled={busyId === m.id}
                                  title="Edit email — syncs to their wallet tiles"
                                  data-testid={`edit-email-btn-${m.id}`}
                                  className="text-ink-3 hover:text-[#4060FF] transition-colors disabled:opacity-40 shrink-0"
                                >
                                  <Pencil size={11} />
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className="chip" style={{ background: chip.bg, color: chip.color }}>
                          {isAdmin && <Shield size={11} style={{ marginRight: 4 }} />}
                          {chip.label}
                        </span>
                      </td>
                      <td>
                        <span className={`chip ${statusChipClass}`} data-testid={`member-status-${m.id}`}>{status}</span>
                        <button type="button" onClick={() => setActivity({ id: m.id, email: m.email, label: m.name || m.email })}
                          title={`Recent sign-ins for ${m.email}`}
                          className="text-[10px] text-ink-3 font-mono mt-0.5 block hover:text-ink-2 underline decoration-dotted decoration-ink-3/40"
                          data-testid={`member-last-login-${m.id}`}>
                          {relTime(m.last_login_at)}
                        </button>
                      </td>
                      <td>
                        {m.role !== "participant" ? (
                          <span className="text-ink-3 text-xs">—</span>
                        ) : m.onboarding_locked ? (
                          <div className="flex flex-col items-start gap-1">
                            {m.onboarding_submitted_at
                              ? <span className="chip chip-blue" data-testid={`member-onb-${m.id}`} title="Checklist complete — waiting for your approval">Awaiting approval</span>
                              : <span className="chip chip-amber" data-testid={`member-onb-${m.id}`} title="Still working through the Getting Started checklist">Setup in progress</span>}
                            {m.sponsor_approved_at
                              ? <span className="chip chip-green text-[10px]" data-testid={`member-sponsor-ok-${m.id}`} title={`Sponsor-approved by ${m.sponsor_approved_by || "sponsor"}`}>Sponsor ✓</span>
                              : <span className="chip chip-gray text-[10px]" data-testid={`member-sponsor-pending-${m.id}`} title="Their sponsor hasn't approved them yet">Sponsor pending</span>}
                          </div>
                        ) : (
                          <span className="chip chip-green" data-testid={`member-onb-${m.id}`} title={m.onboarding_approved_at ? "Approved — full access" : "Grandfathered — full access"}>Unlocked</span>
                        )}
                      </td>
                      <td className="text-xs">
                        {m.has_live_wallet ? (
                          m.node_label || <span className="text-ink-3">—</span>
                        ) : m.node_label ? (
                          <span
                            className="chip chip-amber text-[10px]"
                            data-testid={`member-no-live-wallet-${m.id}`}
                            title={`"${m.node_label}" is a legacy org-chart tile, not a synced CaryPact wallet — that's why this member has no matching row on the Wallets screen.`}
                          >
                            No live wallet
                          </span>
                        ) : (
                          <span
                            className="chip chip-amber text-[10px]"
                            data-testid={`member-no-wallet-${m.id}`}
                            title="No CaryPact wallet + link synced into our tree yet — this account isn't a member until they have their own wallet."
                          >
                            No wallet yet
                          </span>
                        )}
                      </td>
                      <td data-testid={`member-origin-${m.id}`}>
                        <OriginChip origin={m.origin} showPage={false} testid={`member-origin-chip-${m.id}`} />
                      </td>
                      <td className="text-[11px] text-ink-3 font-mono">
                        {relTime(m.created_at)}
                        {user?.is_super_admin && (
                          <span className="block text-[10px]" data-testid={`created-by-${m.id}`}>
                            {m.created_by_label ? `by ${m.created_by_label}` : ""}
                          </span>
                        )}
                      </td>
                      {user?.is_super_admin && (
                        <td data-testid={`access-cell-${m.id}`}>
                          <div className="flex items-center gap-2.5">
                            <label className="inline-flex items-center gap-1 cursor-pointer select-none"
                              title={m.is_super_admin ? "The super admin always sees Operation Momentum" : isDemo ? "The demo account is leaderboard-only" : `Show the Operation Momentum reports to ${m.email}`}>
                              <input
                                type="checkbox"
                                checked={!!m.momentum_access || !!m.is_super_admin}
                                disabled={m.is_super_admin || isDemo || busyId === m.id}
                                onChange={(e) => onToggleMomentum(m, e.target.checked)}
                                data-testid={`momentum-checkbox-${m.id}`}
                                className="w-3.5 h-3.5 rounded cursor-pointer disabled:cursor-not-allowed"
                                style={{ accentColor: "#7C3AED" }}
                              />
                              <Rocket size={11} style={{ color: (m.momentum_access || m.is_super_admin) ? "#7C3AED" : "#CBD5E1" }} />
                            </label>
                            <label className="inline-flex items-center gap-1 cursor-pointer select-none"
                              title={m.is_super_admin ? "The super admin always has the UTM link builder" : isDemo ? "The demo account is leaderboard-only" : `Make ${m.email} a marketing manager — tagged UTM links, wallet stays locked`}>
                              <input
                                type="checkbox"
                                checked={!!m.marketing_access || !!m.is_super_admin}
                                disabled={m.is_super_admin || isDemo || busyId === m.id}
                                onChange={(e) => onToggleMarketing(m, e.target.checked)}
                                data-testid={`marketing-checkbox-${m.id}`}
                                className="w-3.5 h-3.5 rounded cursor-pointer disabled:cursor-not-allowed"
                                style={{ accentColor: "#0EA5E9" }}
                              />
                              <Tag size={11} style={{ color: (m.marketing_access || m.is_super_admin) ? "#0369A1" : "#CBD5E1" }} />
                            </label>
                            <label className="inline-flex items-center gap-1 cursor-pointer select-none"
                              title={isDemo ? "The demo account is leaderboard-only" : m.partner_access
                                ? `${m.email} runs a white-label partner firm — untick to take the portal dark (clients and statements kept)`
                                : `Let ${m.email} run a white-label partner firm — then create it on the Partners screen`}>
                              <input
                                type="checkbox"
                                checked={!!m.partner_access}
                                disabled={isDemo || busyId === m.id}
                                onChange={(e) => onTogglePartner(m, e.target.checked)}
                                data-testid={`partner-checkbox-${m.id}`}
                                className="w-3.5 h-3.5 rounded cursor-pointer disabled:cursor-not-allowed"
                                style={{ accentColor: "#C2185B" }}
                              />
                              <Building2 size={11} style={{ color: m.partner_access ? "#C2185B" : "#CBD5E1" }} />
                            </label>
                          </div>
                        </td>
                      )}
                      <td className="text-right">
                        <div className="inline-flex items-center gap-1 flex-nowrap justify-end">
                          <button
                            type="button"
                            onClick={() => navigate(`/admin/trace?email=${encodeURIComponent(m.email)}`)}
                            title={`Trace ${m.email} — where they came from + full journey`}
                            data-testid={`trace-btn-${m.id}`}
                            className="inline-flex items-center justify-center w-7 h-7 rounded-lg transition-colors"
                            style={{ background: "rgba(124,58,237,0.10)", color: "#7C3AED" }}
                          >
                            <Fingerprint size={14} />
                          </button>
                          <button
                            type="button"
                            onClick={() => onImpersonate(m)}
                            disabled={m.is_super_admin || isSelf || busyId === m.id || isInactive || isDemo}
                            title={m.is_super_admin ? "The super admin can't be impersonated" : isDemo ? "The demo account can't be impersonated — share its password instead" : isSelf ? "That's you" : isInactive ? "Reactivate the account first" : `View as ${m.email}`}
                            data-testid={`impersonate-btn-${m.id}`}
                            className="inline-flex items-center justify-center w-7 h-7 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                            style={{ background: m.is_super_admin || isSelf || isInactive || isDemo ? "rgba(15,18,38,0.05)" : "rgba(64,96,255,0.10)", color: m.is_super_admin || isSelf || isInactive || isDemo ? "#94A3B8" : "#4060FF" }}
                          >
                            <Eye size={14} />
                          </button>
                          {!isDemo && !isSelf && !m.is_super_admin && ["participant", "affiliate_manager", "admin"].includes(m.role) && (
                            <select
                              value={m.role}
                              onChange={(e) => onSetRole(m, e.target.value)}
                              disabled={busyId === m.id}
                              title="Change role — Members see their dashboard, Leaders see only their own branch, Admins manage their team"
                              data-testid={`set-role-select-${m.id}`}
                              className="px-1.5 h-7 rounded-lg text-[11px] font-semibold cursor-pointer disabled:opacity-40 outline-none"
                              style={{ background: "rgba(124,58,237,0.08)", color: "#7C3AED", border: "1px solid rgba(124,58,237,0.25)" }}
                            >
                              <option value="participant">Member</option>
                              <option value="affiliate_manager">Leader</option>
                              <option value="admin">Admin</option>
                            </select>
                          )}
                          {m.role === "participant" && (
                            <button
                              type="button"
                              onClick={() => onToggleOnboarding(m, !!m.onboarding_locked)}
                              disabled={busyId === m.id}
                              title={m.onboarding_locked ? `Approve ${m.email} — unlock the full app` : `Re-lock ${m.email} to the Getting Started checklist`}
                              data-testid={`onboarding-btn-${m.id}`}
                              className="inline-flex items-center justify-center w-7 h-7 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                              style={{
                                background: m.onboarding_locked ? "rgba(0,134,58,0.10)" : "rgba(15,18,38,0.05)",
                                color: m.onboarding_locked ? "#00863A" : "#64748B",
                              }}
                            >
                              {m.onboarding_locked ? <ShieldCheck size={14} /> : <GraduationCap size={14} />}
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => openResetModal(m)}
                            disabled={isProtectedAdmin || busyId === m.id}
                            title={isProtectedAdmin ? "The super admin's password uses 'Change password' while logged in" : isDemo ? "Set the demo password — share it privately with whoever you want" : `Reset password for ${m.email}`}
                            data-testid={`reset-pw-btn-${m.id}`}
                            className="inline-flex items-center justify-center w-7 h-7 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                            style={{ background: isProtectedAdmin ? "rgba(15,18,38,0.05)" : isDemo ? "rgba(124,58,237,0.12)" : "rgba(180,83,9,0.10)", color: isProtectedAdmin ? "#94A3B8" : isDemo ? "#7C3AED" : "#B45309" }}
                          >
                            <KeyRound size={14} />
                          </button>
                          <button
                            type="button"
                            onClick={() => onToggleStatus(m)}
                            disabled={isProtectedAdmin || isSelf || busyId === m.id}
                            title={isProtectedAdmin ? "The super admin can't be deactivated" : isSelf ? "That's you" : isInactive ? `Reactivate ${m.email}` : `Deactivate ${m.email}`}
                            data-testid={`toggle-status-btn-${m.id}`}
                            className="inline-flex items-center justify-center w-7 h-7 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                            style={{
                              background: isProtectedAdmin || isSelf
                                ? "rgba(15,18,38,0.05)"
                                : isInactive ? "rgba(0,134,58,0.10)" : "rgba(220,38,38,0.10)",
                              color: isProtectedAdmin || isSelf ? "#94A3B8" : isInactive ? "#00863A" : "#B91C1C",
                            }}
                          >
                            {isInactive ? <UserCheck size={14} /> : <UserX size={14} />}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        <AdminSupportChats />

        <AuditLogCard />

        {error && (
          <div className="mb-6 chip chip-amber inline-flex items-center gap-2" data-testid="members-error">
            <AlertTriangle size={13} /> {error}
          </div>
        )}

        <div className="glass overflow-hidden mb-6" data-testid="otp-peek-card">
          <div className="p-5 border-b border-black/5 flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: "rgba(245,158,11,0.12)" }}>
              <KeyRound size={16} style={{ color: "#B45309" }} />
            </div>
            <div>
              <div className="kpi-label">Active sign-in codes · auto-refresh 8s</div>
              <h3 className="font-display text-xl font-semibold tracking-tight">Pending OTPs</h3>
            </div>
            <div className="ml-auto text-xs text-ink-3 max-w-md text-right hidden md:block">
              Members get a 6-digit code by email on each new browser. While Resend domain verification is pending, you can share codes manually from here.
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="modern" data-testid="otp-peek-table">
              <thead>
                <tr>
                  <th>Member</th>
                  <th>Code</th>
                  <th>Expires in</th>
                  <th>Attempts</th>
                  <th>Started</th>
                </tr>
              </thead>
              <tbody>
                {otps.length === 0 ? (
                  <tr><td colSpan="5" className="text-center py-8 text-ink-3" data-testid="otp-empty">No pending sign-ins right now.</td></tr>
                ) : otps.map((o) => {
                  const mm = Math.floor((o.ttl_seconds || 0) / 60);
                  const ss = (o.ttl_seconds || 0) % 60;
                  return (
                    <tr key={o.session_id} data-testid={`otp-row-${o.session_id}`}>
                      <td className="text-xs font-mono">{o.email}</td>
                      <td>
                        <div className="inline-flex items-center gap-2">
                          <span className="num font-mono font-bold" style={{ fontSize: 17, letterSpacing: 3, color: "#B45309" }} data-testid={`otp-code-${o.session_id}`}>{o.code || "—"}</span>
                          {o.code && (
                            <button onClick={() => copyCode(o.code)} className="org-icon-btn" title="Copy code" data-testid={`otp-copy-${o.session_id}`}>
                              {copiedCode === o.code ? <Check size={13} style={{ color: "#00863A" }} /> : <Copy size={13} />}
                            </button>
                          )}
                        </div>
                      </td>
                      <td className="text-xs font-mono num">{mm}:{String(ss).padStart(2, "0")}</td>
                      <td className="text-xs">
                        <span className={`chip ${o.attempts >= 3 ? "chip-amber" : "chip-gray"}`}>{o.attempts}/5</span>
                      </td>
                      <td className="text-xs text-ink-3 font-mono">{relTime(o.created_at)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        <div className="glass overflow-hidden" data-testid="audit-log-card">
          <div className="p-5 border-b border-black/5 flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: "rgba(220,38,38,0.10)" }}>
              <Clock size={16} style={{ color: "#B91C1C" }} />
            </div>
            <div>
              <div className="kpi-label">Audit trail · last 25 sessions</div>
              <h3 className="font-display text-xl font-semibold tracking-tight">Impersonation history</h3>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="modern" data-testid="audit-table">
              <thead>
                <tr>
                  <th>Admin</th>
                  <th>Viewed as</th>
                  <th>Started</th>
                  <th>Ended</th>
                  <th>Duration</th>
                  <th>IP</th>
                </tr>
              </thead>
              <tbody>
                {audit.length === 0 ? (
                  <tr><td colSpan="6" className="text-center py-10 text-ink-3" data-testid="audit-empty">No impersonation sessions yet.</td></tr>
                ) : audit.map((a) => {
                  const start = new Date(a.started_at);
                  const end = a.ended_at ? new Date(a.ended_at) : null;
                  const durationMs = end ? end - start : null;
                  const duration = durationMs == null ? <span className="chip chip-amber">Active</span>
                    : durationMs < 60000 ? `${Math.round(durationMs / 1000)}s`
                    : durationMs < 3600000 ? `${Math.round(durationMs / 60000)}m`
                    : `${(durationMs / 3600000).toFixed(1)}h`;
                  return (
                    <tr key={a.id} data-testid={`audit-row-${a.id}`}>
                      <td className="text-xs font-mono">{a.admin_email}</td>
                      <td className="text-xs">
                        <div className="font-mono">{a.target_email}</div>
                        <div className="text-ink-3">{a.target_name || "—"} · {a.target_role}</div>
                      </td>
                      <td className="text-xs text-ink-3 font-mono">{start.toLocaleString()}</td>
                      <td className="text-xs text-ink-3 font-mono">{end ? end.toLocaleString() : "—"}</td>
                      <td className="text-xs num font-semibold">{duration}</td>
                      <td className="text-xs text-ink-3 font-mono">{a.ip || "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        <div className="mt-6 glass p-4 flex items-start gap-3" style={{ background: "rgba(64,96,255,0.04)" }}>
          <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0" style={{ background: "rgba(64,96,255,0.12)" }}>
            <Users size={16} style={{ color: "#4060FF" }} />
          </div>
          <div className="text-xs text-ink-2 leading-relaxed">
            <div className="font-semibold text-ink mb-1">How &ldquo;view as&rdquo; works</div>
            New members appear here automatically once they&apos;re granted a login from the Org Chart. <b>Impersonation sessions have full access</b> — anything you do while viewing as a member is applied for real and recorded in the activity log with both identities. All sessions, including IP &amp; user-agent, are logged below for audit.
            <div className="mt-2 inline-flex items-center gap-1.5 text-ink-3"><Mail size={11} /> Tip: grant logins from <code className="text-[11px]">Org Chart → tile → Team login</code>.</div>
          </div>
        </div>
      </div>
      <HelpFab />

      {activity && <LoginActivityModal who={activity} onClose={() => setActivity(null)} />}

      {resetState && (
        <div
          className="fixed inset-0 z-[200] flex items-center justify-center p-4"
          style={{ background: "rgba(15,18,38,0.55)", backdropFilter: "blur(8px)" }}
          onClick={() => !resetState.busy && setResetState(null)}
          data-testid="reset-pw-modal"
        >
          <div
            className="glass p-6 w-full max-w-md relative"
            onClick={(e) => e.stopPropagation()}
            style={{ background: "rgba(255,255,255,0.98)" }}
          >
            <button
              type="button"
              onClick={() => !resetState.busy && setResetState(null)}
              className="absolute top-3 right-3 org-icon-btn"
              data-testid="reset-pw-close"
              title="Close"
            >
              <X size={15} />
            </button>

            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: "rgba(180,83,9,0.12)" }}>
                <KeyRound size={18} style={{ color: "#B45309" }} />
              </div>
              <div>
                <div className="kpi-label">Admin tool</div>
                <h3 className="font-display text-xl font-semibold tracking-tight">Reset password</h3>
              </div>
            </div>

            <div className="text-sm text-ink-2 mb-4">
              Set a new password for <b className="text-ink font-mono">{resetState.member.email}</b>. The member can use it as-is to log in — they are <b>not</b> forced to change it, and their next sign-in <b>won&apos;t require an email code</b>.
            </div>

            {resetState.mode === "choose" && (
              <>
                <label className="kpi-label mb-1.5 block">New password (optional)</label>
                <input
                  type="text"
                  value={resetState.custom}
                  onChange={(e) => setResetState((s) => ({ ...s, custom: e.target.value, error: "" }))}
                  placeholder="Leave blank to generate a strong one"
                  className="w-full px-3 py-2 rounded-xl border outline-none text-sm font-mono"
                  style={{ borderColor: "rgba(15,18,38,0.10)", background: "rgba(15,18,38,0.03)" }}
                  data-testid="reset-pw-input"
                  disabled={resetState.busy}
                  autoFocus
                />
                <div className="text-[11px] text-ink-3 mt-1.5">
                  Min 8 chars · ≥1 uppercase · ≥1 number · ≥1 special character.
                </div>
                {resetState.error && (
                  <div className="mt-3 chip chip-amber inline-flex items-center gap-2" data-testid="reset-pw-error">
                    <AlertTriangle size={12} /> {resetState.error}
                  </div>
                )}
                <div className="flex items-center gap-2 mt-5">
                  <button
                    type="button"
                    onClick={() => setResetState(null)}
                    className="btn-secondary !py-2 !px-3 text-sm flex-1"
                    disabled={resetState.busy}
                    data-testid="reset-pw-cancel"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={submitReset}
                    disabled={resetState.busy}
                    className="btn-primary !py-2 !px-3 text-sm flex-1 inline-flex items-center justify-center gap-1.5"
                    data-testid="reset-pw-submit"
                  >
                    <KeyRound size={13} /> {resetState.busy ? "Setting…" : (resetState.custom.trim() ? "Set password" : "Generate & set")}
                  </button>
                </div>
              </>
            )}

            {resetState.mode === "done" && (
              <>
                <div className="rounded-xl p-3 mb-3" style={{ background: "rgba(0,134,58,0.08)", border: "1px solid rgba(0,134,58,0.20)" }}>
                  <div className="text-xs font-semibold text-ink mb-1.5">New password</div>
                  <div className="flex items-center gap-2">
                    <code
                      className="num font-mono font-bold flex-1 px-3 py-2 rounded-lg select-all"
                      style={{ background: "rgba(255,255,255,0.95)", fontSize: 16, letterSpacing: 0.5, color: "#0F1226" }}
                      data-testid="reset-pw-result"
                    >
                      {resetState.pw}
                    </code>
                    <button
                      type="button"
                      onClick={copyResetPw}
                      className="org-icon-btn"
                      title="Copy password"
                      data-testid="reset-pw-copy"
                    >
                      <Copy size={14} />
                    </button>
                  </div>
                  <div className="text-[11px] text-ink-3 mt-2">
                    Share this with <b className="font-mono text-ink-2">{resetState.member.email}</b> over a secure channel. We won&apos;t show it again.
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setResetState(null)}
                  className="btn-primary !py-2 !px-3 text-sm w-full"
                  data-testid="reset-pw-done"
                >
                  Done
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
